// src/hooks/useSalaryLedger.js
// ─────────────────────────────────────────────────────────
// Hook for salary_monthly_ledger + salary_driver_events.
//
// READ: supabase client (RLS).
// WRITE driver events: supabase client (admin RLS).
// IMPORT + REBUILD: Edge Functions (service_role).
//
// CUTOVER RULE: Pre-2026 is legacy-only. Post-2026 is derived.
// ─────────────────────────────────────────────────────────

import { useCallback, useState } from 'react';
import { supabase } from '../supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const CUTOVER_MONTH = '2026-01-01';

// ── Helpers ──────────────────────────────────────────────

/**
 * Normalize any date string to YYYY-MM-01 (first of month).
 * Uses pure string parsing — NO Date constructor — to avoid timezone shifts.
 *
 * Accepts: 'YYYY-MM-DD', 'YYYY-MM-01', 'YYYY-MM', or ISO datetime strings.
 */
function normalizeMonth(dateStr) {
  if (!dateStr) return dateStr;
  const s = String(dateStr).trim();

  // Match YYYY-MM at the start (covers YYYY-MM-DD, YYYY-MM-DDTHH:MM, YYYY-MM)
  const match = s.match(/^(\d{4})-(\d{1,2})/);
  if (!match) return dateStr; // unparseable — return as-is

  const yyyy = match[1];
  const mm = match[2].padStart(2, '0');
  return `${yyyy}-${mm}-01`;
}

/**
 * Clamp a month string to >= CUTOVER_MONTH for derived operations.
 * Returns the clamped month string.
 */
function clampToCutover(monthStr) {
  const normalized = normalizeMonth(monthStr);
  if (!normalized) return CUTOVER_MONTH;
  return normalized < CUTOVER_MONTH ? CUTOVER_MONTH : normalized;
}

async function getToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

async function callEdgeFunction(name, body, isFormData = false) {
  const token = await getToken();
  const headers = { Authorization: `Bearer ${token}` };
  if (!isFormData) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers,
    body: isFormData ? body : JSON.stringify(body),
  });

  let result;
  try {
    result = await response.json();
  } catch (_) {
    throw new Error(`Edge function ${name} returned non-JSON (status ${response.status})`);
  }

  if (!response.ok || result?.success === false) {
    throw new Error(result?.error || `Edge function ${name} failed (${response.status})`);
  }
  return result;
}

// ─────────────────────────────────────────────────────────

export function useSalaryLedger() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ══════════════════════════════════════════════
  // READ: Ledger
  // ══════════════════════════════════════════════

  const getLedgerRow = useCallback(async (authUserId, year, month) => {
    const monthStr = `${year}-${String(month).padStart(2, '0')}-01`;
    const { data, error: err } = await supabase
      .from('salary_monthly_ledger')
      .select('*')
      .eq('auth_user_id', authUserId)
      .eq('month', monthStr)
      .maybeSingle();
    if (err) console.error('[SalaryLedger] getLedgerRow:', err);
    return data;
  }, []);

  const getLedgerRange = useCallback(async (authUserId, fromMonth, toMonth) => {
    const { data, error: err } = await supabase
      .from('salary_monthly_ledger')
      .select('*')
      .eq('auth_user_id', authUserId)
      .gte('month', fromMonth)
      .lte('month', toMonth)
      .order('month');
    if (err) console.error('[SalaryLedger] getLedgerRange:', err);
    return data || [];
  }, []);

  const getAllUsersForMonth = useCallback(async (year, month) => {
    const monthStr = `${year}-${String(month).padStart(2, '0')}-01`;
    const { data, error: err } = await supabase
      .from('salary_monthly_ledger')
      .select('*')
      .eq('month', monthStr);
    if (err) console.error('[SalaryLedger] getAllUsersForMonth:', err);
    return data || [];
  }, []);

  const getAllForYear = useCallback(async (year) => {
    const { data, error: err } = await supabase
      .from('salary_monthly_ledger')
      .select('*')
      .gte('month', `${year}-01-01`)
      .lte('month', `${year}-12-01`)
      .order('auth_user_id')
      .order('month');
    if (err) console.error('[SalaryLedger] getAllForYear:', err);
    return data || [];
  }, []);

  // ══════════════════════════════════════════════
  // READ: Driver events
  // ══════════════════════════════════════════════

  const getDriverEvents = useCallback(async (authUserId) => {
    const { data, error: err } = await supabase
      .from('salary_driver_events')
      .select('*')
      .eq('auth_user_id', authUserId)
      .order('effective_from', { ascending: false });
    if (err) console.error('[SalaryLedger] getDriverEvents:', err);
    return data || [];
  }, []);

  const getDriverAtMonth = useCallback(async (authUserId, monthDate) => {
    const { data, error: err } = await supabase
      .from('salary_driver_events')
      .select('*')
      .eq('auth_user_id', authUserId)
      .lte('effective_from', monthDate)
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (err) console.error('[SalaryLedger] getDriverAtMonth:', err);
    return data;
  }, []);

  // ══════════════════════════════════════════════
  // WRITE: Driver event (admin RLS)
  // ══════════════════════════════════════════════

  const insertDriverEvent = useCallback(async (event) => {
    const effectiveFrom = normalizeMonth(event.effective_from);

    const { data, error: err } = await supabase
      .from('salary_driver_events')
      .insert({
        auth_user_id:          event.auth_user_id,
        effective_from:        effectiveFrom,
        job_role:              event.job_role,
        job_level:             event.job_level,
        nl_schaal:             event.nl_schaal || null,
        nl_trede:              event.nl_trede ?? null,
        contracted_hours:      event.contracted_hours || 176,
        bonus_q1:              event.bonus_q1 || 0,
        bonus_q2:              event.bonus_q2 || 0,
        bonus_q3:              event.bonus_q3 || 0,
        bonus_q4:              event.bonus_q4 || 0,
        bonus_allocation_mode: event.bonus_allocation_mode || 'monthly_equivalent',
        changed_by:            event.changed_by || null,
        reason:                event.reason || null,
      })
      .select()
      .single();

    if (err) {
      console.error('[SalaryLedger] insertDriverEvent:', err);
      return { success: false, error: err.message };
    }
    return { success: true, data };
  }, []);

  // ══════════════════════════════════════════════
  // REBUILD: via Edge Function (service_role)
  // TRUE RECREATE: Phase 1 deletes stale, Phase 2 regenerates.
  // ALWAYS clamped to >= 2026-01-01 (both client + server).
  // Passes both from_month and to_month to match RPC signature.
  // ══════════════════════════════════════════════

  const rebuildLedger = useCallback(async (authUserId, fromMonth = null, toMonth = null) => {
    setLoading(true);
    setError(null);
    try {
      // Client-side clamp: never request rebuild for pre-2026
      const clampedFrom = clampToCutover(fromMonth);
      const clampedTo = toMonth ? normalizeMonth(toMonth) : null;

      const result = await callEdgeFunction('rebuild-salary-ledger', {
        auth_user_id: authUserId,
        from_month: clampedFrom,
        to_month: clampedTo,     // null → RPC uses rolling horizon
      });
      console.log('[SalaryLedger] Rebuild result:', result);
      return result;
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const rebuildAllLedgers = useCallback(async (fromMonth = null, toMonth = null) => {
    setLoading(true);
    setError(null);
    try {
      // Client-side clamp: never request rebuild for pre-2026
      const clampedFrom = clampToCutover(fromMonth);
      const clampedTo = toMonth ? normalizeMonth(toMonth) : null;

      const result = await callEdgeFunction('rebuild-salary-ledger', {
        all: true,
        from_month: clampedFrom,
        to_month: clampedTo,     // null → RPC uses rolling horizon
      });
      console.log('[SalaryLedger] Rebuild ALL result:', result);

      // rebuild_all_salary_ledgers returns JSONB with { success, deleted, rebuilt, … }
      // Edge function may wrap this — normalize so callers always get the shape
      if (result && typeof result === 'object') {
        return { ...result, success: result.success !== false };
      }
      return result;
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ══════════════════════════════════════════════
  // IMPORT: Legacy CSV via Edge Function
  // ══════════════════════════════════════════════

  const importLegacyCSV = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await callEdgeFunction('import-legacy-salary-csv', formData, true);
      console.log('[SalaryLedger] Import result:', result);
      return result;
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ══════════════════════════════════════════════
  // COMBINED: Insert driver event + rebuild
  // Only rebuilds from cutover date forward.
  // ══════════════════════════════════════════════

  const insertDriverAndRebuild = useCallback(async (event) => {
    setLoading(true);
    setError(null);
    try {
      const driverResult = await insertDriverEvent(event);
      if (!driverResult.success) {
        throw new Error('Driver event insert failed: ' + driverResult.error);
      }

      // Rebuild from the driver's effective date (clamped to cutover)
      const rebuildResult = await rebuildLedger(
        event.auth_user_id,
        event.effective_from
      );

      return { driverResult, rebuildResult };
    } catch (e) {
      setError(e.message);
      return { success: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, [insertDriverEvent, rebuildLedger]);

  // ══════════════════════════════════════════════
  // ADAPTER: Ledger row → getInternalCostRate format
  // ══════════════════════════════════════════════

  const ledgerToInternalCostFormat = useCallback((ledgerRow, driverEvent = null) => {
    if (!ledgerRow) return null;

    const contractedHours = driverEvent?.contracted_hours || 176;
    const baseSalary = ledgerRow.base_salary || 0;
    const bonus = ledgerRow.bonus || 0;
    const adjusted = baseSalary + bonus;

    const q1 = driverEvent?.bonus_q1 || 0;
    const q2 = driverEvent?.bonus_q2 || 0;
    const q3 = driverEvent?.bonus_q3 || 0;
    const q4 = driverEvent?.bonus_q4 || 0;
    const totalAnnualBonus = q1 + q2 + q3 + q4;

    return {
      monthlySalary: baseSalary,
      salaryMarkupFactor: 1,
      monthlyInternalCost: adjusted,
      hourlyInternalCost: adjusted / 176, // Always divide by standard full-time hours
      contractedHours,
      quarterlyBonuses: { q1, q2, q3, q4 },
      totalAnnualBonus,
      monthlyBonusEquivalent: bonus,
      adjustedMonthlySalary: adjusted,
      source: ledgerRow.source === 'legacy_import' ? 'ledger_legacy' : 'ledger_derived',
      locked: ledgerRow.locked,
    };
  }, []);

  return {
    // Read
    getLedgerRow,
    getLedgerRange,
    getAllUsersForMonth,
    getAllForYear,
    getDriverEvents,
    getDriverAtMonth,
    // Write
    insertDriverEvent,
    insertDriverAndRebuild,
    // Rebuild (Edge Function) — TRUE RECREATE
    rebuildLedger,
    rebuildAllLedgers,
    // Import (Edge Function)
    importLegacyCSV,
    // Adapter
    ledgerToInternalCostFormat,
    // State
    loading,
    error,
  };
}
